import { useMutation } from '@platform/api-client';
import { useAuth } from '@platform/auth-client';
import {
  Badge,
  ChoiceChips,
  formatDateTime,
  formatMoney,
  settlementStatusTone,
  subtractAmounts,
  type Column,
} from '@platform/design-system';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { useState } from 'react';
import { KeysetTable } from '../KeysetTable.tsx';
import { carrierNameOf, useCarriers } from '../settings/carriers.ts';
import { usePageContext } from '../usePageContext.ts';
import {
  FINANCE_WRITE_ROLE,
  SETTLEMENT_VIEWS,
  useSettlements,
  type ResolveDisputeBody,
  type Settlement,
  type SettlementView,
} from './finance.ts';
import styles from './Finance.module.css';
import { FinanceFrame } from './FinanceFrame.tsx';
import type { FinanceSection } from './FinanceSections.tsx';
import { ResolveDisputeSheet } from './ResolveDisputeSheet.tsx';
import { TrackingCode } from '../TrackingCode.tsx';

export type DisputesPageProps = {
  api: TypedClient;
  shipping: ShippingClient;
  onSection: (section: FinanceSection) => void;
};

export const DisputesPage = ({ api, shipping, onSection }: DisputesPageProps) => {
  const { t, locale, notify, invalidate, describeError } = usePageContext();
  const canResolve = useAuth().hasRole(FINANCE_WRITE_ROLE);
  const [view, setView] = useState<SettlementView>('disputed');
  const [resolving, setResolving] = useState<Settlement | null>(null);
  const settlements = useSettlements(api, view);
  const carriers = useCarriers(shipping);
  const carrierName = (code: string): string => carrierNameOf(carriers.data?.items, code);
  const money = (row: Settlement, amount: string | null) =>
    amount === null ? '—' : formatMoney({ amount, currency: row.currency }, locale);
  const resolve = useMutation({
    mutationFn: ({ settlement, body }: { settlement: Settlement; body: ResolveDisputeBody }) =>
      api.post('/admin/finance/settlements/{id}/resolve', { path: { id: settlement.id }, body }),
    onSuccess: async (resolved) => {
      notify(t('disputes.resolved', { trackingNumber: resolved.trackingNumber }));
      setResolving(null);
      await invalidate('settlements', 'finance-overview', 'orders');
    },
  });

  const columns: readonly Column<Settlement>[] = [
    {
      id: 'tracking',
      header: t('disputes.trackingNumber'),
      primary: true,
      cell: (row) => <TrackingCode code={row.trackingNumber} />,
    },
    { id: 'carrier', header: t('disputes.carrier'), cell: (row) => carrierName(row.carrierCode) },
    {
      id: 'expected',
      header: t('disputes.expected'),
      numeric: true,
      cell: (row) => money(row, row.expectedAmount),
    },
    {
      id: 'collected',
      header: t('disputes.collected'),
      numeric: true,
      cell: (row) => money(row, row.collectedAmount),
    },
    {
      id: 'difference',
      header: t('disputes.difference'),
      numeric: true,
      cell: (row) =>
        row.collectedAmount === null ? (
          '—'
        ) : (
          <span className={styles['difference']}>
            {money(row, subtractAmounts(row.collectedAmount, row.expectedAmount))}
          </span>
        ),
    },
    { id: 'settledAt', header: t('disputes.since'), cell: (row) => formatDateTime(row.settledAt, locale) },
    {
      id: 'status',
      status: true,
      header: t('disputes.status'),
      cell: (row) => (
        <Badge tone={settlementStatusTone[row.status]}>{t(`disputes.status_${row.status}`)}</Badge>
      ),
    },
  ];

  return (
    <FinanceFrame section="disputes" context={t('disputes.context')} onSection={onSection}>
      <div className={styles['toolbar']}>
        <ChoiceChips
          legend={t('disputes.view')}
          hideLegend
          locale={locale}
          value={view}
          choices={SETTLEMENT_VIEWS.map((value) => ({ value, label: t(`disputes.view_${value}`) }))}
          onChange={setView}
        />
      </div>
      <KeysetTable
        pages={settlements}
        columns={columns}
        rowKey={(row) => row.id}
        onRowClick={
          canResolve && view === 'disputed'
            ? (row) => {
                resolve.reset();
                setResolving(row);
              }
            : undefined
        }
        emptyTitle={view === 'disputed' ? t('disputes.none') : t('disputes.noneResolved')}
        emptyDescription={view === 'disputed' ? t('disputes.noneHelp') : undefined}
      />
      {resolving === null ? null : (
        <ResolveDisputeSheet
          settlement={resolving}
          submitting={resolve.isPending}
          errorMessage={resolve.isError ? (describeError(resolve.error) ?? null) : null}
          onClose={() => {
            setResolving(null);
          }}
          onResolve={(body) => {
            resolve.mutate({ settlement: resolving, body });
          }}
        />
      )}
    </FinanceFrame>
  );
};
